import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import { Image, Link, mergeStyleSets, NeutralColors, SharedColors, Text, Icon, Stack, DefaultButton } from '@fluentui/react';
import { Constants } from '@shared/utils/constants';
import { SpzaInstrumentService } from '@shared/services/telemetry/spza/spzaInstrument';
import { getWindow } from '@shared/services/window';
import { logger } from '@src/logger';
import Visa from '@shared/images/visa.svg';
import AmericanExpress from '@shared/images/americanExpress.svg';
import MasterCard from '@shared/images/masterCard.svg';
import { useBoolean } from '@fluentui/react-hooks';
import { AzureRedirectModal } from '@appsource/components/AzureRedirectModal/AzureRedirectModal';
const contentStyles = mergeStyleSets({
    container: {
        marginTop: '48px',
        display: 'inline-block'
    },
    root: {
        padding: '24px 32px',
        backgroundColor: NeutralColors.gray10,
        border: `1px solid ${NeutralColors.gray30}`,
        borderRadius: 1,
        minHeight: '204px'
    },
    header: {
        fontWeight: 600
    },
    footer: {
        paddingTop: '8px'
    },
    title: {
        maxWidth: '250px'
    },
    primaryTitle: {
        fontWeight: 700,
        color: SharedColors.cyanBlue10
    },
    azureInfoTitle: {
        maxWidth: '270px'
    }
});
export const PaymentMethodsInfo = ({ isAdminUser, isMacc, entityId, isBagOffer }, context)=>{
    const [linkClicked, { toggle: toggleLinkClicked }] = useBoolean(false);
    const checkoutId = useSelector(({ checkout })=>checkout.checkoutId);
    const offerLink = `${Constants.AzurePortal.CreateOfferById}${entityId}`;
    return /*#__PURE__*/ _jsxs(Stack, {
        tokens: {
            childrenGap: 16
        },
        horizontal: true,
        wrap: true,
        children: [
            /*#__PURE__*/ _jsxs(Stack, {
                className: contentStyles.container,
                tokens: {
                    childrenGap: 16
                },
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        className: contentStyles.header,
                        children: context.loc('Choose_payment_method', 'Up next, choose your payment method:')
                    }),
                    /*#__PURE__*/ _jsxs(Stack, {
                        tokens: {
                            childrenGap: 40
                        },
                        horizontal: true,
                        className: contentStyles.root,
                        wrap: true,
                        children: [
                            /*#__PURE__*/ _jsxs(Stack, {
                                tokens: {
                                    childrenGap: 8
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Text, {
                                        className: contentStyles.header,
                                        children: context.loc('Credit_card', 'Credit card')
                                    }),
                                    /*#__PURE__*/ _jsxs(Stack, {
                                        className: contentStyles.title,
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                children: context.loc('Use_credit_card', 'Use a credit card to make an online payment.')
                                            }),
                                            /*#__PURE__*/ _jsx(Text, {
                                                children: context.loc('Accepted_payment_methods', 'Accepted payment methods:')
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Stack, {
                                        className: contentStyles.footer,
                                        tokens: {
                                            childrenGap: 3
                                        },
                                        horizontal: true,
                                        children: [
                                            /*#__PURE__*/ _jsx(Image, {
                                                src: Visa
                                            }),
                                            /*#__PURE__*/ _jsx(Image, {
                                                src: AmericanExpress
                                            }),
                                            /*#__PURE__*/ _jsx(Image, {
                                                src: MasterCard
                                            })
                                        ]
                                    })
                                ]
                            }),
                            isAdminUser && /*#__PURE__*/ _jsxs(Stack, {
                                tokens: {
                                    childrenGap: 8
                                },
                                children: [
                                    /*#__PURE__*/ _jsxs(Stack, {
                                        horizontal: true,
                                        tokens: {
                                            childrenGap: 5
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                className: contentStyles.header,
                                                children: context.loc('Invoice_pay', 'Invoice pay')
                                            }),
                                            /*#__PURE__*/ _jsx(Text, {
                                                className: contentStyles.primaryTitle,
                                                children: context.loc('New_invoice', 'New!')
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Text, {
                                        className: contentStyles.title,
                                        children: context.loc('Invoice_info', 'If your organziation has been approved for payment by invoice, select Invoice pay on the next page to pay with a check or wire transfer.')
                                    }),
                                    /*#__PURE__*/ _jsx(Stack, {
                                        className: contentStyles.footer,
                                        children: /*#__PURE__*/ _jsxs(Link, {
                                            onClick: ()=>{
                                                var _getWindow;
                                                const payload = {
                                                    page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.location.href,
                                                    action: Constants.Telemetry.Action.Click,
                                                    actionModifier: Constants.Telemetry.ActionModifier.ExternalLink,
                                                    details: Constants.InvoiceLink
                                                };
                                                SpzaInstrumentService.getProvider().probe(Constants.Telemetry.ProbeName.LogInfo, payload);
                                                logger.info(payload.details, payload);
                                            },
                                            underline: true,
                                            href: Constants.InvoiceLink,
                                            target: "_blank",
                                            children: [
                                                context.loc('Learn_more_invoice', 'Learn more about paying by invoice '),
                                                /*#__PURE__*/ _jsx(Icon, {
                                                    iconName: "NavigateExternalInline"
                                                })
                                            ]
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            isMacc && !isBagOffer && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(Stack, {
                        className: contentStyles.container,
                        tokens: {
                            childrenGap: 16
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                className: contentStyles.header,
                                children: context.loc('AzureSubscription_header', 'Or if you have an Azure subscription:')
                            }),
                            /*#__PURE__*/ _jsx(Stack, {
                                className: contentStyles.root,
                                children: /*#__PURE__*/ _jsxs(Stack, {
                                    tokens: {
                                        childrenGap: 8
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            className: contentStyles.header,
                                            children: context.loc('AzureSubscription', 'Azure subscription')
                                        }),
                                        /*#__PURE__*/ _jsxs(Text, {
                                            className: contentStyles.azureInfoTitle,
                                            children: [
                                                context.loc('AzureSubscription_info', 'Use your Azure subscription to purchase this app in Azure Marketplace, and benefit your organization by reducing your existing Azure commitment.'),
                                                /*#__PURE__*/ _jsx(Link, {
                                                    onClick: ()=>{
                                                        var _getWindow;
                                                        const payload = {
                                                            page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.location.href,
                                                            action: Constants.Telemetry.Action.Click,
                                                            actionModifier: Constants.Telemetry.ActionModifier.ExternalLink,
                                                            details: Constants.AzureSubscriptionLink
                                                        };
                                                        SpzaInstrumentService.getProvider().probe(Constants.Telemetry.ProbeName.LogInfo, payload);
                                                        logger.info(payload.details, payload);
                                                    },
                                                    target: "_blank",
                                                    href: Constants.AzureSubscriptionLink,
                                                    children: context.loc('App_LearnMore', 'Learn more')
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Stack, {
                                            className: contentStyles.footer,
                                            horizontal: true,
                                            children: /*#__PURE__*/ _jsx(DefaultButton, {
                                                onClick: ()=>{
                                                    var _getWindow;
                                                    const payload = {
                                                        page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.location.href,
                                                        action: Constants.Telemetry.Action.Checkout,
                                                        actionModifier: Constants.Telemetry.ActionModifier.AzureRedirect,
                                                        details: JSON.stringify({
                                                            offerLink,
                                                            checkoutId
                                                        })
                                                    };
                                                    SpzaInstrumentService.getProvider().probe(Constants.Telemetry.ProbeName.LogInfo, payload);
                                                    logger.info(payload.details, payload);
                                                    toggleLinkClicked();
                                                },
                                                name: context.loc('AzureSubscription_get_it', 'Get it in Azure portal'),
                                                text: context.loc('AzureSubscription_get_it', 'Get it in Azure portal'),
                                                target: "_blank",
                                                href: offerLink,
                                                children: /*#__PURE__*/ _jsx(Icon, {
                                                    iconName: "NavigateExternalInline"
                                                })
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(AzureRedirectModal, {
                        linkClicked: linkClicked,
                        onDismiss: ()=>toggleLinkClicked()
                    })
                ]
            })
        ]
    });
};
PaymentMethodsInfo.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func
};
